import * as React from 'react';

import { Unit, Weight } from '@rematter/frontend-lib-api-client';
import { enumUnit } from '@rematter/frontend-lib-api-client';
import {
  convertToPounds,
  convertFromPounds,
  humanReadableShortPricingUnit,
} from '@rematter/ui-web';

import { WeightUnitType } from '~/modules/trade/types/load';

export interface UseWeightConversionProps {
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  pricingUnit?: string;
}

export const useWeightConversion = ({
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  pricingUnit,
}: UseWeightConversionProps = {}) => {
  // Determine display unit based on weight unit toggle and pricing unit
  const getDisplayUnit = React.useCallback(
    (pricingUnit: Unit | null | undefined): Unit => {
      const currentPricingUnit = pricingUnit || enumUnit.Pounds;

      // Exception: "Each" always displays as "Each" regardless of toggle
      if (currentPricingUnit === enumUnit.Each) return enumUnit.Each;

      // Otherwise respect the toggle: 'price' uses pricing unit, 'scale' uses facility default
      return weightUnit === 'price' ? currentPricingUnit : facilityDefaultUnit;
    },
    [weightUnit, facilityDefaultUnit],
  );

  const displayUnit = React.useMemo(
    () => getDisplayUnit(pricingUnit as Unit),
    [getDisplayUnit, pricingUnit],
  );

  // Convert stored weight value to display unit for showing in UI
  const convertToDisplayUnit = React.useCallback(
    (weight: Weight | null | undefined): number => {
      if (!weight?.value) return 0;

      // If units are the same, no conversion needed
      if (weight.unit === displayUnit) return weight.value;

      // Convert: stored unit → pounds → display unit
      const pounds = convertToPounds(weight.value, weight.unit as Unit);
      return convertFromPounds(pounds, displayUnit);
    },
    [displayUnit],
  );

  // Convert display value back to stored unit for saving
  const convertFromDisplayUnit = React.useCallback(
    (value: number, storedUnit: Unit): number => {
      // If units are the same, no conversion needed
      if (storedUnit === displayUnit) return value;

      // Convert: display unit → pounds → stored unit
      const pounds = convertToPounds(value, displayUnit);
      return convertFromPounds(pounds, storedUnit);
    },
    [displayUnit],
  );

  const massUnitAbbreviation = React.useMemo(
    () => humanReadableShortPricingUnit(displayUnit),
    [displayUnit],
  );

  return {
    getDisplayUnit,
    convertToDisplayUnit,
    convertFromDisplayUnit,
    massUnitAbbreviation,
  };
};
